import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const DAY_NAMES_ES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb']

function todayStr() { return new Date().toISOString().slice(0,10) }
function shiftDate(base, dir) {
  const d = new Date(base+'T12:00:00'); d.setDate(d.getDate()+dir); return d.toISOString().slice(0,10)
}

async function sendSMS(to, message) {
  const res = await fetch('/.netlify/functions/send-sms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, message }),
  })
  return res.json()
}

function getTemplate(key, defaultTmpl) {
  return localStorage.getItem(key) || defaultTmpl
}

function buildClientMessage(job, client, dateStr) {
  const d = new Date(dateStr+'T12:00:00')
  const month = d.toLocaleDateString('en-US', { month: 'long' })
  const day = d.getDate()
  const dayName = d.toLocaleDateString('en-US', { weekday: 'long' })
  const h = job.start_time || '9:00 AM'
  const [hm, ap] = h.split(' ')
  const [hr, min] = hm.split(':').map(Number)
  const endMin = min + 15
  const endMinStr = String(endMin % 60).padStart(2,'0')
  const endHr = hr + Math.floor(endMin / 60)
  const endTime = `${endHr % 12 || 12}:${endMinStr} ${ap}`
  const firstName = (client.nickname || client.name || '').split(' ')[0]

  const tmpl = getTemplate('sms_client_template', `Hi {nombre}! This is a reminder from Kitty's Cleaning Services 🐱. We will arrive on {dia} between {hora} - {ventana}. See you soon!`)
  return tmpl
    .replace('{nombre}', firstName)
    .replace('{dia}', `${dayName}, ${month} ${day}`)
    .replace('{hora}', h)
    .replace('{ventana}', endTime)
}

function buildStaffMessage(staffMember, routeJobs, clients, dateStr, isUpdate, routeLabel, baseUrl) {
  const d = new Date(dateStr+'T12:00:00')
  const dayName = DAY_NAMES_ES[d.getDay()]
  const dateDisplay = d.toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })
  const firstName = (staffMember.name || '').split(' ')[0]
  const routeLink = `${baseUrl}?route=${routeLabel}&date=${dateStr}&name=${encodeURIComponent(staffMember.name||'')}`

  const stops = routeJobs.map((job, i) => {
    const client = clients.find(c => c.id === job.client_id)
    const name = client?.nickname || client?.name || 'Cliente'
    const addr = client?.address ? `📍 ${client.address}` : ''
    const note = job.one_time_note ? `📝 ${job.one_time_note}` : ''
    return [`${i+1}. ${name} — ${job.start_time}`, addr, note].filter(Boolean).join('\n   ')
  }).join('\n\n')

  const normalTmpl = getTemplate('sms_staff_template', `Buenos días {nombre}! 🐱 Tu ruta del {dia}:\n\n{paradas}\n\n🔗 Ver ruta completa:\n{link}`)
  const updateTmpl = getTemplate('sms_update_template', `Perdón {nombre}, hubo un cambio en tu ruta de {dia}. Este es el horario actualizado:\n\n{paradas}\n\n🔗 Ver ruta actualizada:\n{link}`)
  const tmpl = isUpdate ? updateTmpl : normalTmpl

  return tmpl
    .replace('{nombre}', firstName)
    .replace('{dia}', `${dayName} ${dateDisplay}`)
    .replace('{paradas}', stops)
    .replace('{link}', routeLink)
}

export default function SMS() {
  const [jobs, setJobs] = useState([])
  const [clients, setClients] = useState([])
  const [staff, setStaff] = useState([])
  const [routes, setRoutes] = useState([])
  const [dateStr, setDateStr] = useState(todayStr())
  const [sending, setSending] = useState({})
  const [sentLog, setSentLog] = useState({}) // { key: { time, isUpdate } }
  const [customMsg, setCustomMsg] = useState({})
  const [loading, setLoading] = useState(true)

  const baseUrl = window.location.origin

  useEffect(() => { fetchDay() }, [dateStr])

  // Load sent log from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`sms_log_${dateStr}`)
    if (saved) try { setSentLog(JSON.parse(saved)) } catch(e) {}
  }, [dateStr])

  async function fetchDay() {
    setLoading(true)
    const [{ data: jb }, { data: cl }, { data: st }, { data: rt }] = await Promise.all([
      supabase.from('jobs').select('*').eq('scheduled_date', dateStr).order('start_time'),
      supabase.from('clients').select('*'),
      supabase.from('staff').select('*').eq('is_active', true).order('name'),
      supabase.from('routes').select('*').eq('route_date', dateStr),
    ])
    setJobs(jb||[])
    setClients(cl||[])
    setStaff(st||[])
    setRoutes(rt||[])
    setLoading(false)
  }

  function getRouteStaff(routeLabel) {
    const saved = routes.find(r => r.label === routeLabel)
    if (!saved?.notes) return []
    try { return JSON.parse(saved.notes).staffIds || [] } catch(e) { return [] }
  }

  function confirmSend(key, to, message, name) {
    setConfirmModal({ key, to, message, name })
  }

  function copyMessage(key, message) {
    navigator.clipboard.writeText(message).then(() => {
      const log = { ...sentLog, [key]: { time: new Date().toLocaleTimeString('es-MX', { hour:'2-digit', minute:'2-digit' }), isUpdate: key.includes('-update') } }
      setSentLog(log)
      localStorage.setItem(`sms_log_${dateStr}`, JSON.stringify(log))
    })
  }

  // Build client sections
  const clientRows = jobs.map(job => {
    const client = clients.find(c => c.id === job.client_id)
    const key = `client-${job.id}`
    const defaultMsg = client ? buildClientMessage(job, client, dateStr) : ''
    return { job, client, key, defaultMsg }
  })

  // Build staff sections — group jobs by route, then by staff
  const routeLabels = [...new Set(jobs.map(j => j.route).filter(Boolean))].sort()
  const staffRows = staff.map(st => {
    const staffRoutes = routeLabels.filter(label => getRouteStaff(label).includes(st.id))
    const allJobs = staffRoutes.flatMap(label => jobs.filter(j => j.route === label))
    const primaryRoute = staffRoutes[0] || 'A'
    const keyNormal = `staff-${st.id}`
    const keyUpdate = `staff-${st.id}-update`
    const defaultMsg = buildStaffMessage(st, allJobs, clients, dateStr, false, primaryRoute, baseUrl)
    const updateMsg = buildStaffMessage(st, allJobs, clients, dateStr, true, primaryRoute, baseUrl)
    return { st, allJobs, keyNormal, keyUpdate, defaultMsg, updateMsg, primaryRoute }
  })

  const d = new Date(dateStr+'T12:00:00')
  const dateLabel = `${DAY_NAMES_ES[d.getDay()]}, ${d.toLocaleDateString('es-MX', { month:'short', day:'numeric' })}`

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <div style={{fontSize:15,fontWeight:600,color:'#202124'}}>📱 SMS</div>
        <div style={{display:'flex',alignItems:'center',gap:6,marginLeft:12}}>
          <button style={s.nbtn} onClick={() => setDateStr(d => shiftDate(d,-1))}>←</button>
          <span style={{fontSize:13,fontWeight:500,minWidth:110,textAlign:'center'}}>{dateLabel}</span>
          <button style={s.nbtn} onClick={() => setDateStr(d => shiftDate(d,1))}>→</button>
        </div>
        <div style={{marginLeft:'auto',fontSize:11,color:'#9aa0a6'}}>
          {Object.keys(sentLog).length} enviados hoy
        </div>
      </div>

      {loading ? <div style={{padding:32,textAlign:'center',color:'#9aa0a6'}}>Cargando...</div> : (
        <div style={s.body}>
          <div style={s.grid}>

            {/* CLIENT CONFIRMATIONS */}
            <div>
              <div style={s.sectionHead}>
                <span style={s.sectionTitle}>🏠 Confirmaciones a clientes</span>
                <span style={{fontSize:11,color:'#9aa0a6'}}>{clientRows.length} limpiezas · English</span>
              </div>
              {clientRows.length === 0 && <div style={s.empty}>Sin limpiezas programadas</div>}
              {clientRows.map(({ job, client, key, defaultMsg }) => {
                const sent = sentLog[key]
                const isSending = sending[key]
                const msg = customMsg[key] ?? defaultMsg
                return (
                  <div key={key} style={s.card}>
                    <div style={s.cardHead}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500}}>{client?.nickname||client?.name||'Cliente'}</div>
                        <div style={{fontSize:11,color:'#80868b'}}>{job.start_time} · {client?.phone||<span style={{color:'#e24b4a'}}>Sin teléfono</span>}</div>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:3}}>
                        {sent && <span style={{fontSize:10,color:'#1d9e75',fontWeight:500}}>✓ Enviado {sent.time}</span>}
                      </div>
                    </div>
                    <textarea style={s.msgArea} value={msg}
                      onChange={e => setCustomMsg(p => ({...p,[key]:e.target.value}))} rows={3}/>
                    <div style={{display:'flex',gap:6}}>
                      {sent && (
                        <button style={{...s.sendBtn,background:'#185fa5',flex:'0 0 auto'}}
                          onClick={() => {
                            setCustomMsg(p => ({...p,[key]:msg}))
                            confirmSend(key+'-update', client?.phone, msg, client?.nickname||client?.name)
                          }} disabled={isSending||!client?.phone}>
                          🔄 Update
                        </button>
                      )}
                      <button style={{...s.sendBtn,opacity:!client?.phone?0.4:1,flex:1}}
                        onClick={() => copyMessage(key, msg)}
                        disabled={isSending||!client?.phone}>
                        {sent ? '✓ Copiado' : '📋 Copiar'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* STAFF MESSAGES */}
            <div>
              <div style={s.sectionHead}>
                <span style={s.sectionTitle}>👩 Horarios al personal</span>
                <span style={{fontSize:11,color:'#9aa0a6'}}>Español · Con link de ruta</span>
              </div>
              {staffRows.map(({ st, allJobs, keyNormal, keyUpdate, defaultMsg, updateMsg, primaryRoute }) => {
                const sent = sentLog[keyNormal]
                const isSending = sending[keyNormal] || sending[keyUpdate]
                const msg = customMsg[keyNormal] ?? defaultMsg
                const upMsg = customMsg[keyUpdate] ?? updateMsg
                const hasRoute = allJobs.length > 0

                return (
                  <div key={st.id} style={{...s.card,...(!hasRoute?{opacity:0.5}:{})}}>
                    <div style={s.cardHead}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500}}>{st.name}{st.role==='lead'?' ★':''}</div>
                        <div style={{fontSize:11,color:'#80868b'}}>
                          {st.phone || <span style={{color:'#e24b4a'}}>Sin teléfono</span>}
                          {hasRoute ? ` · ${allJobs.length} paradas` : ' · Sin ruta asignada'}
                        </div>
                      </div>
                      {sent && <span style={{fontSize:10,color:'#1d9e75',fontWeight:500}}>✓ Enviado {sent.time}</span>}
                    </div>
                    {!hasRoute && <div style={{fontSize:11,color:'#9aa0a6',marginBottom:8,fontStyle:'italic'}}>Asigna esta empleada a una ruta primero</div>}
                    <textarea style={s.msgArea} value={msg}
                      onChange={e => setCustomMsg(p => ({...p,[keyNormal]:e.target.value}))} rows={5}/>
                    <div style={{display:'flex',gap:6}}>
                      {/* Update button — sends "Perdón hubo un cambio" message */}
                      <button style={{...s.sendBtn,background:'#185fa5',flex:'0 0 auto',fontSize:11}}
                        onClick={() => copyMessage(keyUpdate, upMsg)}
                        disabled={isSending||!st.phone||!hasRoute}
                        title="Envía mensaje de actualización con el texto: Perdón hubo un cambio...">
                        🔄 Nuevo
                      </button>
                      <button style={{...s.sendBtn,flex:1,opacity:!st.phone||!hasRoute?0.4:1}}
                        onClick={() => copyMessage(keyNormal, msg)}
                        disabled={isSending||!st.phone||!hasRoute}>
                        {sent ? '✓ Copiado' : '📋 Copiar'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Setup instructions */}
          <div style={s.setupBox}>
            <div style={{fontWeight:600,marginBottom:6,color:'#333',fontSize:12}}>⚙️ Configuración Twilio (solo una vez)</div>
            <div style={{fontSize:11,color:'#5f6368',lineHeight:1.9}}>
              1. Crea cuenta en <a href="https://console.twilio.com" target="_blank" rel="noreferrer" style={{color:'#e8547a'}}>console.twilio.com</a> · 2. Obtén número (~$1/mes) · 3. Ve a Netlify → Site config → Environment variables · 4. Agrega: <code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_PHONE_NUMBER</code> · 5. Redeploy ✓
            </div>
          </div>
        </div>
      )}


    </div>
  )
}

const s = {
  wrap:{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'},
  topbar:{display:'flex',alignItems:'center',gap:6,padding:'10px 16px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0},
  nbtn:{padding:'3px 10px',border:'1px solid #e3e5e8',borderRadius:8,background:'#f8f9fa',cursor:'pointer',fontSize:14,color:'#5f6368'},
  body:{flex:1,overflowY:'auto',padding:'14px 16px'},
  grid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16},
  sectionHead:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10},
  sectionTitle:{fontSize:13,fontWeight:600,color:'#202124'},
  card:{background:'#fff',borderRadius:10,border:'1px solid #e3e5e8',padding:'12px',marginBottom:10},
  cardHead:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8},
  msgArea:{width:'100%',fontSize:11,padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,resize:'vertical',background:'#f8f9fa',color:'#333',fontFamily:'inherit',marginBottom:8,lineHeight:1.6},
  sendBtn:{padding:'8px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer',fontSize:12},
  empty:{fontSize:12,color:'#9aa0a6',padding:16,textAlign:'center'},
  setupBox:{padding:'12px 14px',background:'#f8f8f8',borderRadius:10,fontSize:11},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200},
  modal:{background:'#fff',borderRadius:14,padding:'20px 22px',width:360,boxShadow:'0 8px 32px rgba(0,0,0,0.15)'},
}
